import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { ArrowsClockwise, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

const nowMonth = () => new Date().toISOString().slice(0, 7);

export default function MonthlyPnL() {
    const [month, setMonth] = useState(nowMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("statement");   // "statement" | "unit"
    const [ue, setUe] = useState(null);
    const [ueLoading, setUeLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/reports/monthly-pnl/${month}`);
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [month]);
    useEffect(() => { load(); }, [load]);

    const loadUe = useCallback(async () => {
        setUeLoading(true);
        try {
            const { data } = await api.get(`/reports/unit-economics/${month}`);
            setUe(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUeLoading(false); }
    }, [month]);
    useEffect(() => { if (tab === "unit") loadUe(); }, [tab, loadUe]);

    if (!data) {
        return (
            <>
                <Toaster richColors position="top-right" />
                <PageHeader title="Monthly P&L" sub="Loading…" />
            </>
        );
    }

    if (data.before_start_month) {
        return (
            <>
                <Toaster richColors position="top-right" />
                <Header month={month} setMonth={setMonth} loading={loading} onReload={load} />
                <div className="p-8">
                    <div className="p-6 border rounded-md bg-amber-50 text-amber-900" data-testid="pnl-before-start">
                        <div className="flex items-center gap-2 mb-2">
                            <WarningCircle size={18} weight="duotone" />
                            <b>Monthly P&amp;L starts from {data.start_month}.</b>
                        </div>
                        Choose a later month, or update the start month in Settings.
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Toaster richColors position="top-right" />
            <Header month={month} setMonth={setMonth} loading={loading} onReload={load} />
            <div className="p-8 space-y-6 max-w-5xl">

                {/* View toggle — Statement vs Unit economics */}
                <div className="inline-flex rounded-md border overflow-hidden"
                     style={{ borderColor: "var(--se-border)" }}
                     data-testid="pnl-view-toggle">
                    {[
                        { key: "statement", label: "Statement view" },
                        { key: "unit",      label: "Unit economics" },
                    ].map((t) => (
                        <button key={t.key}
                                onClick={() => setTab(t.key)}
                                className="px-4 py-1.5 text-xs font-medium"
                                style={{
                                    background: tab === t.key ? "var(--se-brand)" : "#fff",
                                    color: tab === t.key ? "#fff" : "#111",
                                }}
                                data-testid={`pnl-tab-${t.key}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "unit" ? (
                    <UnitEconomicsView ue={ue} loading={ueLoading} month={month} />
                ) : (<>

                <div className={`p-4 border rounded-md text-sm ${data.finalised ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}
                     data-testid="pnl-status-strip">
                    <div className="flex items-center gap-2">
                        {data.finalised
                            ? <CheckCircle size={16} weight="fill" />
                            : <WarningCircle size={16} weight="fill" />}
                        <b>{data.finalised ? "Finalised" : "Draft — awaiting closing stock"}</b>
                        <span className="text-stone-600 text-[11px] ml-auto">
                            Monthly P&L finalises once closing stock is entered (periodic-inventory). Live P&L is coming later.
                        </span>
                    </div>
                    {!data.finalised ? (
                        <div className="mt-1 text-xs">
                            The numbers below assume <b>zero</b> closing stock. Enter closing stock in the <Link to="/stock" className="underline" data-testid="pnl-go-stock">Stock</Link> page to finalise.
                        </div>
                    ) : null}
                </div>

                {/* Revenue */}
                <Section title="Revenue" total={data.revenue.total} testid="pnl-revenue" accent="#059669">
                    <LedgerList items={data.revenue.items} testid="pnl-revenue-list" month={data.month} emptyLabel="No invoices posted this month" />
                </Section>

                {/* COGS */}
                <Section title="Cost of Goods Sold" total={data.cogs} testid="pnl-cogs" accent="#dc2626" formula>
                    <table className="se-table text-sm">
                        <tbody>
                            <tr data-testid="pnl-opening-row">
                                <td className="font-medium" style={{ width: "40%" }}>
                                    Opening stock
                                    <span className="ml-2 text-[10px] text-stone-500">
                                        ({data.opening_stock.source === "inherited_from_prev_month"
                                            ? `inherited from ${data.opening_stock.detail?.month}`
                                            : data.opening_stock.source === "manual"
                                                ? "entered"
                                                : "none — treated as 0"})
                                    </span>
                                </td>
                                <td className="se-num text-right">₹ {inr(data.opening_stock.total)}</td>
                            </tr>
                            <tr data-testid="pnl-direct-row">
                                <td className="font-medium">
                                    <span className="text-emerald-700">+</span> Direct purchases (per Settings)
                                </td>
                                <td className="se-num text-right">₹ {inr(data.direct_purchases.total)}</td>
                            </tr>
                            <tr data-testid="pnl-closing-row">
                                <td className="font-medium">
                                    <span className="text-rose-700">−</span> Closing stock
                                    <span className="ml-2 text-[10px] text-stone-500">
                                        ({data.closing_stock.source === "manual" ? "entered" : "not yet entered"})
                                    </span>
                                </td>
                                <td className="se-num text-right">₹ {inr(data.closing_stock.total)}</td>
                            </tr>
                            <tr className="font-medium" style={{ borderTop: "2px solid #cbd5e1", background: "#fef2f2" }}>
                                <td>= COGS</td>
                                <td className="se-num text-right" data-testid="pnl-cogs-total">₹ {inr(data.cogs)}</td>
                            </tr>
                        </tbody>
                    </table>
                    {data.direct_purchases.items.length > 0 ? (
                        <details className="mt-3">
                            <summary className="text-xs text-stone-500 cursor-pointer">Direct purchases breakdown ({data.direct_purchases.items.length} ledgers)</summary>
                            <LedgerList items={data.direct_purchases.items} testid="pnl-direct-list" month={data.month} />
                        </details>
                    ) : null}
                </Section>

                {/* Gross Margin */}
                <BigLine label="Gross margin" value={data.gross_margin} testid="pnl-gross-margin" />

                {/* Indirect Expenses */}
                <Section title="Indirect expenses" total={data.indirect_expenses.total} testid="pnl-indirect" accent="#f97316">
                    <IndirectExpensesView items={data.indirect_expenses.items} month={data.month} total={data.indirect_expenses.total} />
                </Section>

                {/* Net */}
                <BigLine label="Net profit" value={data.net_profit} testid="pnl-net" bold />

                {/* Channel split — GST business vs Fiat business */}
                {data.channels ? <ChannelBreakdown ch={data.channels} /> : null}

                <div className="text-[11px] text-stone-500 px-1">
                    Note: Revenue is ex-GST — GST output ledgers sit under Liabilities and don&apos;t hit revenue.
                    COGS uses the direct-purchase ledger list from <Link to="/settings" className="underline" data-testid="pnl-go-settings">Settings</Link>.
                </div>
                </>)}
            </div>
        </>
    );
}

function UnitEconomicsView({ ue, loading, month }) {
    if (loading || !ue) {
        return <div className="p-6 text-sm text-stone-500">Loading unit economics…</div>;
    }
    const p = ue.planned || {};
    const a = ue.actual || {};
    const nu = (v) => v == null ? "—" : `₹ ${inr(v)}`;
    const pct = (v) => v == null ? "—" : `${v.toFixed(1)}%`;

    const rows = [
        { k: "Selling price (incl. tax)",              p: nu(p.selling_price_incl_tax),  a: nu(a.selling_price_incl_tax) },
        { k: "Taxable revenue",                         p: nu(p.taxable_revenue),         a: nu(a.taxable_revenue) },
        { k: "COGS + Labour",                           p: nu(p.cogs_and_labour),         a: nu(a.cogs_and_labour) },
        { k: "as % of taxable", italic: true,           p: pct(p.cogs_pct),               a: pct(a.cogs_pct) },
        { k: "GROSS MARGIN", bold: true,                p: nu(p.gross_margin),            a: nu(a.gross_margin) },
        { k: "as % of taxable", italic: true, hero: true, p: pct(p.gross_margin_pct),     a: pct(a.gross_margin_pct) },
        { k: "Operating costs (Transport + Txn + Warranty)", p: nu(p.operating_costs),    a: nu(a.operating_costs) },
        { k: "as % of taxable", italic: true,           p: pct(p.operating_costs_pct),    a: pct(a.operating_costs_pct) },
        { k: "Sales & marketing (CAC + Commission)",    p: nu(p.sales_and_marketing),     a: nu(a.sales_and_marketing) },
        { k: "as % of taxable", italic: true,           p: pct(p.sales_and_marketing_pct), a: pct(a.sales_and_marketing_pct) },
        { k: "CONTRIBUTION MARGIN (CM2)", bold: true,   p: nu(p.cm2),                     a: nu(a.cm2) },
        { k: "as % of taxable", italic: true, hero: true, p: pct(p.cm2_pct),              a: pct(a.cm2_pct) },
    ];

    return (
        <div className="space-y-4" data-testid="unit-economics">
            <div className="rounded-md border p-3 flex items-center gap-4 flex-wrap"
                 style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Units this month</div>
                    <div className="se-num text-lg font-medium" data-testid="ue-units">{ue.units}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Invoices</div>
                    <div className="se-num text-lg">{ue.invoice_count}</div>
                </div>
                <div className="text-xs text-stone-500 ml-auto">
                    Planned assumptions live in{" "}
                    <Link to="/settings" className="underline" data-testid="ue-go-settings">Settings → Unit economics</Link>.
                    Fiat sales are counted at same taxable = incl. tax (0% GST).
                </div>
            </div>

            <div className="bg-white border rounded-md overflow-hidden"
                 style={{ borderColor: "var(--se-border)" }}
                 data-testid="ue-table-card">
                <div className="px-4 py-2 text-white text-sm font-medium tracking-wide"
                     style={{ background: "#57443a" }}>
                    <span>Assumptions vs Actual — {month}</span>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                            <th className="px-4 py-2 text-left font-medium">Line item</th>
                            <th className="px-4 py-2 text-right font-medium" style={{ width: "22%" }}>PLANNED</th>
                            <th className="px-4 py-2 text-right font-medium" style={{ width: "22%" }}>ACTUAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-t"
                                style={{ borderColor: "var(--se-border)",
                                         background: r.hero ? "#faf5ff" : (r.italic ? "#fafaf9" : "#fff") }}
                                data-testid={`ue-row-${i}`}>
                                <td className={`px-4 py-2 ${r.italic ? "italic text-stone-500 pl-8 text-xs" : r.bold ? "font-medium uppercase tracking-wider text-[13px]" : ""}`}>
                                    {r.k}
                                </td>
                                <td className={`px-4 py-2 se-num text-right ${r.bold ? "font-semibold" : r.italic ? "text-stone-500 text-xs" : ""}`}>
                                    {r.p}
                                </td>
                                <td className={`px-4 py-2 se-num text-right ${r.bold ? "font-semibold" : r.italic ? "text-stone-500 text-xs" : ""}`}
                                    style={{ color: r.italic || !r.bold ? undefined : ((a.cm2 ?? 0) >= (p.cm2 ?? 0) ? "#0f766e" : "#9f1239") }}>
                                    {r.a}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {ue.units === 0 ? (
                <div className="rounded-md p-3 text-xs"
                     style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#78350f" }}>
                    No posted sales this month yet — post a normal or fiat invoice and the Actual column will fill in.
                </div>
            ) : null}
        </div>
    );
}

function Header({ month, setMonth, loading, onReload }) {
    return (
        <PageHeader
            title="Monthly P&L"
            sub="Periodic-inventory basis. Revenue − COGS = Gross Margin. Less indirect expenses = Net."
            right={
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-stone-500">Month</Label>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                           className="w-40" data-testid="pnl-month-picker" />
                    <Button variant="outline" onClick={onReload} disabled={loading} data-testid="pnl-refresh">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                </div>
            }
        />
    );
}

function Section({ title, total, children, testid, accent, formula }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-5 py-3 border-b flex items-center justify-between"
                 style={{ borderColor: "var(--se-border)", borderTop: `3px solid ${accent}` }}>
                <div className="font-serif text-lg" style={{ color: accent }}>{title}</div>
                {!formula ? (
                    <div className="se-num text-xl font-medium" data-testid={`${testid}-total`}>
                        ₹ {inr(total)}
                    </div>
                ) : null}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function LedgerList({ items, testid, emptyLabel = "Nothing posted", month }) {
    if (!items || items.length === 0) {
        return <div className="text-xs text-stone-500 py-3">{emptyLabel}</div>;
    }
    return (
        <table className="se-table text-sm" data-testid={testid}>
            <thead>
                <tr><th>Ledger</th><th>Group</th><th className="text-right">Amount</th></tr>
            </thead>
            <tbody>
                {items.map((r) => (
                    <tr key={r.ledger_id} data-testid={`${testid}-row-${r.ledger_id}`}>
                        <td className="font-medium">
                            <Link to={`/ledgers/${r.ledger_id}${month ? `?month=${month}` : ""}`}
                                  className="hover:underline text-blue-700"
                                  title={`Drill into ${r.ledger_name} — ${month}`}>
                                {r.ledger_name}
                            </Link>
                        </td>
                        <td className="text-xs text-stone-500">{r.group_name}</td>
                        <td className="se-num text-right">₹ {inr(r.amount)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function BigLine({ label, value, testid, bold }) {
    const color = value < 0 ? "#dc2626" : "#059669";
    return (
        <div className="border rounded-md px-6 py-5 bg-white flex items-center justify-between"
             style={{ borderColor: "var(--se-border)", borderLeft: `4px solid ${color}` }} data-testid={testid}>
            <div className={`font-serif ${bold ? "text-2xl" : "text-xl"}`} style={{ color: "#0f172a" }}>{label}</div>
            <div className={`se-num ${bold ? "text-3xl" : "text-2xl"} font-medium`} style={{ color }} data-testid={`${testid}-value`}>
                {value < 0 ? "−" : ""}₹ {inr(Math.abs(value))}
            </div>
        </div>
    );
}


// Palette for the indirect-expenses bar chart — warm, distinct, and calm.
const CHART_COLORS = ["#f97316", "#ea580c", "#dc2626", "#c2410c", "#9a3412", "#f59e0b", "#b45309", "#78350f"];

function ChartTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-stone-200 rounded-md px-3 py-2 shadow-sm text-xs">
            <div className="font-medium text-stone-800">{d.full}</div>
            <div className="text-stone-500">{d.group}</div>
            <div className="se-num mt-1 text-stone-900">₹ {inr(d.amount)} · {d.pct.toFixed(1)}%</div>
        </div>
    );
}

function IndirectExpensesView({ items, month, total }) {
    if (!items || items.length === 0) {
        return <div className="text-xs text-stone-500 py-3">No indirect expenses this month</div>;
    }
    // Top 8 for the chart; remainder rolled into "Others"
    const sorted = [...items].sort((a, b) => b.amount - a.amount);
    const top = sorted.slice(0, 8);
    const rest = sorted.slice(8);
    const chartData = top.map((r) => ({
        name: r.ledger_name.length > 22 ? r.ledger_name.slice(0, 20) + "…" : r.ledger_name,
        full: r.ledger_name,
        group: r.group_name,
        amount: r.amount,
        pct: total > 0 ? (r.amount / total) * 100 : 0,
    }));
    if (rest.length > 0) {
        const restTotal = rest.reduce((s, r) => s + r.amount, 0);
        chartData.push({
            name: `Others (${rest.length})`,
            full: `${rest.length} smaller heads`,
            group: "—",
            amount: restTotal,
            pct: total > 0 ? (restTotal / total) * 100 : 0,
        });
    }

    return (
        <div className="space-y-4" data-testid="pnl-indirect-view">
            <div className="bg-stone-50 border border-stone-200 rounded-md p-3" data-testid="pnl-indirect-chart">
                <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2 flex items-center justify-between">
                    <span>Where the money went</span>
                    <span>{items.length} ledger{items.length === 1 ? "" : "s"}</span>
                </div>
                <div style={{ width: "100%", height: Math.max(180, chartData.length * 34) }}>
                    <ResponsiveContainer>
                        <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="name" width={160}
                                   tick={{ fontSize: 11, fill: "#57534e" }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: "rgba(249,115,22,0.06)" }} content={<ChartTooltip />} />
                            <Bar dataKey="amount" radius={[0, 4, 4, 0]}
                                 label={{
                                     position: "right",
                                     formatter: (v) => `₹ ${inr(v)}`,
                                     fill: "#57534e",
                                     fontSize: 11,
                                 }}>
                                {chartData.map((row, i) => (
                                    <Cell key={row.name || `cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <details>
                <summary className="text-xs text-stone-500 cursor-pointer hover:text-stone-700"
                         data-testid="pnl-indirect-toggle">
                    Full breakdown ({items.length} ledgers)
                </summary>
                <div className="mt-2">
                    <LedgerList items={items} testid="pnl-indirect-list" month={month} />
                </div>
            </details>
        </div>
    );
}


function ChannelBreakdown({ ch }) {
    // ch = { revenue|direct_purchases|cogs|gross_margin|indirect_expenses|net_profit : {gst, fiat, total} }
    const hasFiat = Math.abs(ch.revenue?.fiat || 0) > 0.5
        || Math.abs(ch.direct_purchases?.fiat || 0) > 0.5
        || Math.abs(ch.indirect_expenses?.fiat || 0) > 0.5;
    return (
        <div className="mt-3 rounded-md border overflow-hidden"
             style={{ borderColor: "var(--se-border)", background: "#fff" }}
             data-testid="pnl-channels">
            <div className="px-5 py-3 border-b flex items-center justify-between"
                 style={{ borderColor: "var(--se-border)", background: "linear-gradient(90deg, #fef3c7 0%, #fee2e2 100%)" }}>
                <div>
                    <div className="font-serif text-lg">Channel breakdown — GST business vs Fiat business</div>
                    <div className="text-xs text-stone-600">
                        Fiat = parallel zero-GST cash business. Everything flows through FIAT BANK.{" "}
                        {!hasFiat ? "No Fiat activity this month." : null}
                    </div>
                </div>
                <a href="/fiat" className="text-xs font-medium underline" style={{ color: "#9f1239" }} data-testid="pnl-fiat-link">
                    Open Fiat vouchers →
                </a>
            </div>
            <table className="se-table">
                <thead>
                    <tr>
                        <th></th>
                        <th className="text-right">GST business</th>
                        <th className="text-right">Fiat business</th>
                        <th className="text-right">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <ChannelRow label="Revenue"           row={ch.revenue}           positive testid="ch-revenue" />
                    <ChannelRow label="− Direct purchases" row={ch.direct_purchases}  negative testid="ch-direct" />
                    <ChannelRow label="= COGS"            row={ch.cogs}              negative testid="ch-cogs"    subtle />
                    <ChannelRow label="Gross margin"      row={ch.gross_margin}      positive testid="ch-gross"   bold />
                    <ChannelRow label="− Indirect expenses" row={ch.indirect_expenses} negative testid="ch-indirect" />
                    <ChannelRow label="Net profit"        row={ch.net_profit}        signed   testid="ch-net"     bold big />
                </tbody>
            </table>
        </div>
    );
}

function ChannelRow({ label, row, positive, negative, signed, subtle, bold, big, testid }) {
    if (!row) return null;
    const cell = (v) => {
        if (Math.abs(v) < 0.005) return <span className="text-stone-400">—</span>;
        let color = "#0f172a";
        if (positive) color = v > 0 ? "#0f766e" : "#9f1239";
        else if (negative) color = v > 0 ? "#9f1239" : "#0f766e";
        else if (signed)  color = v >= 0 ? "#0f766e" : "#9f1239";
        return <span style={{ color }}>₹ {inr(v)}</span>;
    };
    return (
        <tr data-testid={testid} className={subtle ? "opacity-70" : ""}
            style={bold ? { background: "#fafaf9", fontWeight: 500, borderTop: "2px solid #cbd5e1" } : {}}>
            <td style={big ? { fontSize: "1rem" } : {}}>{label}</td>
            <td className="se-num text-right" data-testid={`${testid}-gst`}>{cell(row.gst)}</td>
            <td className="se-num text-right" data-testid={`${testid}-fiat`}>{cell(row.fiat)}</td>
            <td className="se-num text-right" data-testid={`${testid}-total`}>{cell(row.total)}</td>
        </tr>
    );
}
